import { NgClass, NgTemplateOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component, HostBinding, Input, OnChanges, Optional, SimpleChanges, TemplateRef } from '@angular/core';

import { TotalsPrefix } from '@cds/betting-offer';
import { AutomationModule } from '@frontend/sports/automation/core-feature';
import { EventModel, EventOption, EventOptionGroup, EventParticipantImage, SetTab } from '@frontend/sports/betting-offer/feature/model';
import { MarketGroupingConfig, Sitecore, SitecoreEventDetailsConfig } from '@frontend/sports/common/client-config-data-access';
import { FontResizerComponent } from '@frontend/sports/common/core/feature/font-resizer';
import { NumberDictionary } from '@frontend/sports/common/core/utils/extended-types';
import { NativePriceFormatterPipe } from '@frontend/sports/odds/feature/native-price';
import { NOT_APPLICABLE, TrackingSource } from '@frontend/sports/tracking/feature';
import { NativeAppService, UrlService } from '@frontend/vanilla/core';
import { IconCustomComponent } from '@frontend/vanilla/shared/icons';
import { toNumber } from 'lodash-es';

import { SourceOfPick } from '../betslip-base/models/pick-models';
import { ShowInEpcotDirective } from '../common/epcot-config.directive';
import { EpcotConfigService } from '../common/epcot-config.service';
import { EventPickComponent } from '../option-pick/event-pick.component';
import { PriceboostCountSignpostComponent } from '../shared-priceboost/Components/priceboost-count-signpost.component';
import { OptionContextService } from '../shared-priceboost/Services/option-context.service';
import { OptionNameStrategy } from './option-name.strategy';
import { PlayerStatsComponent } from './player-stats/player-stats.component';

@Component({
    selector: 'ms-option',
    templateUrl: 'option.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        ShowInEpcotDirective,
        NgTemplateOutlet,
        NgClass,
        PlayerStatsComponent,
        EventPickComponent,
        AutomationModule,
        PriceboostCountSignpostComponent,
        IconCustomComponent,
        FontResizerComponent,
        NativePriceFormatterPipe,
    ],
    styleUrl: './option.component.scss',
})
export class OptionComponent implements OnChanges {
    @Input() event: EventModel;
    @Input() option: EventOption;
    @Input() optionGroup: EventOptionGroup;
    @Input() tracking: TrackingSource;
    @Input() showPromoBadge = false;
    @Input() showOptionName = true;
    @Input() showSeperateOptionName?: boolean;
    @Input() sourceOfPick = SourceOfPick.Other;
    @Input() betDetails?: string;
    @Input() showOptionAttribute = false;
    @Input() optionImages: NumberDictionary<EventParticipantImage> = {};
    @Input() imageTemplate: TemplateRef<any>;
    @Input() isShowOptionImage = false;
    @Input() showPlayerStats = false;

    @HostBinding('class') className = 'option';

    optionBetDetails: NumberDictionary<string>;
    optionName: string;
    isEpcotEnabled = false;

    constructor(
        private optionNameStrategy: OptionNameStrategy,
        private urlService: UrlService,
        private sitecore: Sitecore,
        private sitecoreEventDetails: SitecoreEventDetailsConfig,
        @Optional() private optionContextService: OptionContextService,
        private marketGroupingConfig: MarketGroupingConfig,
        private nativeAppService: NativeAppService,
        private epcotConfigService: EpcotConfigService,
    ) {
        this.isEpcotEnabled = this.epcotConfigService.isEnabled();
    }

    private getBetDetails(option: EventOption, betDetails: string[] | undefined[]): string {
        if (this.sourceOfPick !== SourceOfPick.EventDetails && this.sourceOfPick !== SourceOfPick.TeamGrouping) {
            return this.betDetails || NOT_APPLICABLE;
        }

        if (this.optionGroup.detailedGrouping?.marketTabId && this.optionGroup.detailedGrouping?.optionColumnId) {
            betDetails[2] = option.optionColumnName;
        }

        return betDetails.join(';');
    }

    updateOptionBetDetails(): void {
        const betDetailsMap: NumberDictionary<string> = {};

        const betDetails: string[] | undefined[] = [];

        if (this.sourceOfPick === SourceOfPick.EventDetails || this.sourceOfPick === SourceOfPick.TeamGrouping) {
            if (this.optionGroup.detailedGrouping?.marketTabId && this.optionGroup.detailedGrouping?.optionColumnId) {
                betDetails[1] = this.optionGroup.periodName;
            } else {
                betDetails[1] = this.optionGroup.name;
            }
            const activeMarketTabId = toNumber(this.urlService.current().search.get('market'));
            switch (activeMarketTabId) {
                case SetTab.All:
                    betDetails[0] = this.sitecoreEventDetails.eventDetails.AllMarketsTemplate;
                    break;
                case SetTab.MyMarkets:
                    betDetails[0] = this.sitecoreEventDetails.eventDetails.MyMarkets;
                    break;
                case SetTab.MyBets:
                    betDetails[0] = this.sitecore.globalMessages.MyBetsTitle;
                    break;
                default: {
                    const name = this.event.optionSets.find((o) => o.id === activeMarketTabId)?.name ?? NOT_APPLICABLE;
                    betDetails[0] = this.event.optionSets.length > 1 ? name : NOT_APPLICABLE;
                    break;
                }
            }
        }
        for (const option of this.optionGroup.options) {
            let _betDetails = betDetails;
            betDetailsMap[option.id] = this.getBetDetails(option, _betDetails);
            _betDetails = [];
        }

        this.optionBetDetails = betDetailsMap;
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.sourceOfPick || changes.optionGroup || changes.event) {
            this.updateOptionBetDetails();
        }

        if (changes.option) {
            this.optionName = this.getOptionName();
        }
    }

    getOptionName(): string {
        const optionName = this.optionNameStrategy.getOptionName(this.option);
        if (
            this.marketGroupingConfig.isOverUnderAbbreviationEnabled &&
            (this.option.marketAttribute || this.option.optionAttribute) &&
            this.option.totalsPrefix
        ) {
            const prefix =
                this.option.totalsPrefix === TotalsPrefix.Over
                    ? this.sitecore.eventGrid.SixPack_TotalsOver
                    : this.sitecore.eventGrid.SixPack_TotalsUnder;

            return `${prefix} ${this.option.marketAttribute || this.option.optionAttribute}`;
        }

        if (this.isPriceBoost && this.nativeAppService.isTerminal) {
            return `${this.option.optionMarketName} - ${optionName}`;
        }

        if (this.showOptionAttribute) {
            return this.option.optionAttribute!;
        }

        return optionName;
    }

    get isPriceBoost(): boolean {
        return !!this.optionContextService?.isPriceBoost && !!this.option.showBoostedPrice;
    }
}
